import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Data } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { deviceWalletsStateAtom } from '@root/features/secure-storage/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { WalletErrorCode } from '@root/shared/api/api-error-code'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'
import { waitAsyncStateAction } from '@root/shared/lib'

import type { WalletAddress, WalletId } from '../model'
import type { TypeHelper } from '@blade/std-lib'
import type {
  WalletStoreWalletCreatePayload,
  WalletTradeStatsCreatePayload,
} from '@blade/trade-api-client'
import type { SecureStorageError } from '@root/features/secure-storage/secure-storage-error'
import type { WalletOnDevice } from '@root/features/secure-storage/secure-storage-service'
import type { UnknownError } from '@root/shared'
import type { WalletApi } from '@root/shared/api'

export const enrichWalletsRequestAction = action(
  async (
    ctx,
    network: Network.Network,
    deviceWallets: ReadonlyArray<WalletOnDevice>,
  ): Promise<
    Result.Result<Array<WalletApi.WalletDto>, UnknownError | SecureStorageError>
  > => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletEnrichWalletsCreate, [
        {
          network: Network.toDto(network),
          status: 'active',
        },
        deviceWallets
          .filter((deviceWallet) => deviceWallet.network === network)
          .map((x) => ({
            walletId: x.walletId,
            walletAddress: x.address,
          })),
      ]),
    )

    const mappedResult = result.pipe(
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'enrichWalletsRequestAction',
)

export const fetchWalletsRequestAction = action(
  async (
    ctx,
    network: Network.Network,
  ): Promise<
    Result.Result<Array<WalletApi.WalletDto>, UnknownError | SecureStorageError>
  > => {
    const deviceWallets = await ctx.schedule(() =>
      waitAsyncStateAction(ctx, deviceWalletsStateAtom),
    )

    /*
     * A device that cannot answer has no wallets on it — it is not an account
     * with no wallets.
     *
     * The secure store only exists in the desktop build, so in a browser this
     * read always fails, and failing the whole list with it is what made a
     * web session show nothing at all: the account's server-held wallets were
     * sitting on the other side of a request that was never made. The device's
     * list is an addition to the account's, so when there is no device to ask,
     * we ask for the account's alone.
     */
    const onThisDevice = Result.isSuccess(deviceWallets)
      ? deviceWallets.success
      : []

    return enrichWalletsRequestAction(ctx, network, onThisDevice)
  },
  'fetchWalletsRequestAction',
)

export class WalletAlreadyExistsError extends Data.TaggedError(
  'WalletAlreadyExistsError',
) {}

export const storeWalletRequestAction = action(
  async (
    ctx,
    {
      network,
      ...args
    }: TypeHelper.OverrideKeys<
      WalletStoreWalletCreatePayload,
      {
        network: Network.Network
        walletId: WalletId
        walletAddress: WalletAddress
      }
    >,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletStoreWalletCreate, [
        {
          ...args,
          network: Network.toDto(network),
        },
      ]),
    )

    return result.pipe(
      Result.mapError((error) => {
        if (ApiError.isOfCode(WalletErrorCode.WalletAlreadyAdded)(error)) {
          return new WalletAlreadyExistsError()
        }
        return ApiError.fallbackToUnknown(error)
      }),
    )
  },
  'storeWalletRequestAction',
)

type FetchWalletTradeStatsRequestArgs = TypeHelper.OverrideKeys<
  WalletTradeStatsCreatePayload,
  {
    walletIds: Array<WalletId>
  }
>
export const fetchWalletTradeStatsRequestAction = action(
  async (ctx, { walletIds }: FetchWalletTradeStatsRequestArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletTradeStatsCreate, [
        {
          walletIds,
          userTimezone: tz,
        },
      ]),
    )
    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchWalletTradeStatsRequestAction',
)

export const deleteWalletRequestAction = action(
  async (ctx, { walletId }: { walletId: WalletId }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletDelete, [{ id: walletId }]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'deleteWalletRequestAction',
)

export const reorderWalletsRequestAction = action(
  async (
    ctx,
    {
      network,
      walletIds,
    }: { network: Network.Network; walletIds: Array<WalletId> },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.wallet.walletReorderCreate, [
        {
          network: Network.toDto(network),
          walletIds,
        },
      ]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'reorderWalletsRequestAction',
)
